import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { publicClient } from "@/api/publicClient";
import Spinner from "@/components/ui/Spinner";
import AdBuilderCanvas from "@/pages/campaign-detail/components/ad-builder/ad-builder-canvas";

/**
 * Public ad preview: what someone opening a shared preview link sees.
 *
 * No sign-in and no app chrome — only the ad on its canvas, drawn by the same
 * renderer the Ad Builder uses (in read-only mode), so it looks exactly like
 * the internal preview. The API sends nothing beyond the ad itself.
 */

interface PublicPreview {
  ad_type: string;
  canvas: any;
  version: {
    id: string;
    content: Record<string, string>;
    media_json: Record<string, { url: string | null; mime_type: string | null }>;
  };
}

const noop = () => {};

const PublicAdPreview = () => {
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<PublicPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  // View state the canvas renderer needs; nothing here is ever saved.
  const [activeDevice, setActiveDevice] = useState("");
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [carouselSlideIndex, setCarouselSlideIndex] = useState<Record<string, number>>({});
  const [audioPlaying, setAudioPlaying] = useState<Record<string, boolean>>({});
  const [activeTrack, setActiveTrack] = useState<Record<string, number>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const activeInterfaceIdRef = useRef("");

  useEffect(() => {
    document.title = "Ad preview";
    publicClient
      .get(`/public/ad-previews/${token}`)
      .then((response) => setPreview(response.data.data))
      .catch((e: AxiosError<any>) =>
        setError(e.response?.data?.message ?? "This preview link is no longer available."),
      );
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="mx-auto max-w-5xl">
        {error ? (
          <div className="mx-auto mt-24 max-w-md rounded-xl bg-white p-8 text-center shadow-sm">
            <h1 className="text-lg font-semibold text-gray-900">Preview unavailable</h1>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
          </div>
        ) : !preview ? (
          <div className="mt-24 flex justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <p className="mb-4 text-center text-sm text-gray-500">
              Ad preview · {preview.ad_type}
            </p>
            <AdBuilderCanvas
              readOnly
              adBuilderData={{ canvas: preview.canvas }}
              activeDevice={activeDevice}
              setActiveDevice={setActiveDevice}
              showFullScreen={showFullScreen}
              setShowFullScreen={setShowFullScreen}
              commentModeActive={false}
              setCommentModeActive={noop}
              comments={[]}
              activeCommentId={null}
              pendingPin={null}
              isDrawerOpen={false}
              activeVersionId={preview.version.id}
              activeInterfaceIdRef={activeInterfaceIdRef}
              isSaving={false}
              contentBySubMaster={preview.version.content}
              mediaOverrides={{}}
              mediaSlots={[]}
              assetVersions={[{ id: preview.version.id, media_json: preview.version.media_json }]}
              carouselSlideIndex={carouselSlideIndex}
              setCarouselSlideIndex={setCarouselSlideIndex}
              audioPlaying={audioPlaying}
              setAudioPlaying={setAudioPlaying}
              audioRefs={audioRefs}
              activeTrack={activeTrack}
              setActiveTrack={setActiveTrack}
              openDrawer={noop}
              closeDrawer={noop}
              setPendingPin={noop}
              onSaveChanges={noop}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PublicAdPreview;
